import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { getHeaderTheme } from "../../../config/headerTheme";
import Modal from "../../../components/Modal";
import SkeletonLoader from "../../../components/SkeletonLoader";
import { useUser } from "../../../hooks/useUser";
import { useOwnerRestaurantSelection } from "../../../hooks/useOwnerRestaurantSelection";
import { getTenantId } from "../../../utils/tenant";
import { useTenantTopicsAccess } from "../hooks/useTenantTopicsAccess";
import { addTenantTopic } from "../services/tenantTopicsService";
import { getTriviaMarketplacePacks } from "../services/triviaMarketplaceService";
import { redeemPoints } from "../../../services/transactionService";
import { useTransaction } from "../../../hooks/useTransaction";
import { buildQuizHeaders } from "../utils/buildQuizHeaders";
import {
  QuizCardSkeletonGroup,
  QuizEmptyMessage,
  QuizErrorMessage,
  QuizInlineInfo,
  QuizProfileWrapper,
  QuizTopicCard,
  QuizTopicHeader,
} from "../components/QuizProfileLayout";
import QuizCategoryFilter from "../components/QuizCategoryFilter";

const getPackId = (pack) => String(pack?._id || pack?.id || pack?.topic_id || "").trim();
const MARKETPLACE_PAGE_SIZE = 6;
const TRIVIA_COST_POINTS = 100;

const getPageSlice = (items = [], currentPage = 1, pageSize = MARKETPLACE_PAGE_SIZE) => {
  const start = (currentPage - 1) * pageSize;
  return items.slice(start, start + pageSize);
};

const getVisiblePages = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

function MarketplacePagination({
  currentPage,
  totalItems,
  pageSize = MARKETPLACE_PAGE_SIZE,
  onPageChange,
}) {
  const totalPages = Math.ceil(totalItems / pageSize);

  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <PaginationContainer>
      <PaginationInfo>
        Mostrando {startItem}-{endItem} de {totalItems}
      </PaginationInfo>
      <PaginationControls>
        <PaginationButton
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ‹
        </PaginationButton>
        {getVisiblePages(currentPage, totalPages).map((page, index) =>
          page === "..." ? (
            <PaginationInfo key={`ellipsis-${index}`}>...</PaginationInfo>
          ) : (
            <PaginationButton
              key={page}
              type="button"
              $active={page === currentPage}
              onClick={() => onPageChange(page)}
            >
              {page}
            </PaginationButton>
          )
        )}
        <PaginationButton
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          ›
        </PaginationButton>
      </PaginationControls>
    </PaginationContainer>
  );
}

const TriviaMarketplace = () => {
  const { userId } = useUser();
  const { selectedOwnerRestaurant } = useOwnerRestaurantSelection();
  const { getPoints } = useTransaction();

  // === Trivias state ===
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [packsError, setPacksError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [pendingPack, setPendingPack] = useState(null);
  const [localAcquiredPackIds, setLocalAcquiredPackIds] = useState([]);
  const [packsPage, setPacksPage] = useState(1);
  const [showNoBalanceModal, setShowNoBalanceModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const headers = useMemo(() => {
    const storedName = typeof window !== "undefined" ? localStorage.getItem("hname") : "";
    return buildQuizHeaders({
      userId: userId || undefined,
      userName: selectedOwnerRestaurant?.name || selectedOwnerRestaurant?.restoName || storedName || undefined,
    });
  }, [selectedOwnerRestaurant?.name, selectedOwnerRestaurant?.restoName, userId]);

  const {
    topicIds: acquiredTopicIds,
    loading: loadingAccess,
    error: accessError,
    reload: reloadTopicAccess,
  } = useTenantTopicsAccess({ enabled: true });

  const acquiredPackSet = useMemo(
    () => new Set([...(acquiredTopicIds || []), ...localAcquiredPackIds].map((id) => String(id))),
    [acquiredTopicIds, localAcquiredPackIds]
  );

  const loadPacks = useCallback(async () => {
    setLoadingPacks(true);
    setPacksError("");
    try {
      const nextPacks = await getTriviaMarketplacePacks({ headers });
      setPacks(nextPacks);
    } catch (error) {
      setPacksError(error?.message || "No se pudieron cargar los packs de trivias.");
      setPacks([]);
    } finally {
      setLoadingPacks(false);
    }
  }, [headers]);

  useEffect(() => {
    loadPacks();
  }, [loadPacks]);

  const categories = useMemo(
    () => Array.from(new Set(packs.map((pack) => pack.category?.name).filter(Boolean))),
    [packs]
  );

  const filteredPacks = useMemo(
    () => (selectedCategory ? packs.filter((pack) => pack.category?.name === selectedCategory) : packs),
    [packs, selectedCategory]
  );

  const sortedFilteredPacks = useMemo(
    () =>
      [...filteredPacks].sort((a, b) => {
        const aAcquired = acquiredPackSet.has(getPackId(a)) ? 1 : 0;
        const bAcquired = acquiredPackSet.has(getPackId(b)) ? 1 : 0;
        return aAcquired - bAcquired;
      }),
    [filteredPacks, acquiredPackSet]
  );

  const paginatedPacks = useMemo(
    () => getPageSlice(sortedFilteredPacks, packsPage, MARKETPLACE_PAGE_SIZE),
    [sortedFilteredPacks, packsPage]
  );

  useEffect(() => {
    setPacksPage(1);
  }, [packs.length, selectedCategory]);

  // === Trivias handlers ===
  const handleOpenAcquireModal = (pack) => {
    setActionError("");
    setActionSuccess("");
    setPendingPack(pack || null);
  };

  const handleCloseAcquireModal = () => {
    if (actionLoadingId) return;
    setPendingPack(null);
  };

  const handleAcquirePack = async () => {
    const packId = getPackId(pendingPack);
    const tenantId = getTenantId();

    if (actionLoadingId) return;
    if (!packId) {
      setActionError("No se pudo identificar el pack seleccionado.");
      return;
    }
    if (!tenantId) {
      setActionError("No se pudo identificar el tenant para agregar el pack.");
      return;
    }

    setActionLoadingId(packId);
    setActionError("");
    setActionSuccess("");
    try {
      await redeemPoints({
        amount: TRIVIA_COST_POINTS,
        reason: `Compra pack: ${pendingPack?.name || "Trivia"}`,
      });

      await getPoints();

      await addTenantTopic(tenantId, packId);
      setLocalAcquiredPackIds((prev) => (prev.includes(packId) ? prev : [...prev, packId]));
      await reloadTopicAccess();
      setActionSuccess(`Pack adquirido: ${pendingPack?.name || "Trivia"}.`);
      setPendingPack(null);
    } catch (error) {
      const msg = error?.message || "";
      if (msg.toLowerCase().includes("saldo insuficiente")) {
        setPendingPack(null);
        setShowNoBalanceModal(true);
      } else {
        setActionError(msg || "No se pudo completar la compra.");
      }
    } finally {
      setActionLoadingId("");
    }
  };

  const isTriviasLoading = loadingPacks || loadingAccess;
  const triviasVisibleError = packsError || accessError;

  return (
    <QuizProfileWrapper>
      <MarketplaceHeader>
        <h2>Marketplace</h2>
        <p>Canjea tus puntos por nuevas trivias, aprende herramientas para tu negocio y sigue acumulando recompensas para crecer.</p>
      </MarketplaceHeader>

      {triviasVisibleError && <QuizErrorMessage>{triviasVisibleError}</QuizErrorMessage>}
      {actionError && <QuizErrorMessage>{actionError}</QuizErrorMessage>}
      {actionSuccess && <QuizInlineInfo>{actionSuccess}</QuizInlineInfo>}

      {!isTriviasLoading && !triviasVisibleError && categories.length > 0 && (
        <QuizCategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onSelect={setSelectedCategory}
        />
      )}

      {isTriviasLoading && (
        <QuizCardSkeletonGroup>
          <SkeletonLoader active width="100%" height="128px" borderRadius="12px" />
          <SkeletonLoader active width="100%" height="128px" borderRadius="12px" />
          <SkeletonLoader active width="100%" height="128px" borderRadius="12px" />
        </QuizCardSkeletonGroup>
      )}

      {!isTriviasLoading && !triviasVisibleError && packs.length === 0 && (
        <QuizEmptyMessage>No hay packs de trivias disponibles por ahora.</QuizEmptyMessage>
      )}

      {!isTriviasLoading && !triviasVisibleError && packs.length > 0 && filteredPacks.length === 0 && (
        <QuizEmptyMessage>No hay packs en esta categoría.</QuizEmptyMessage>
      )}

      {!isTriviasLoading && !triviasVisibleError && packs.length > 0 && (
        <>
          <PacksGrid>
            {paginatedPacks.map((pack, index) => {
              const packId = getPackId(pack);
              const isAcquired = acquiredPackSet.has(packId);
              const isActionLoading = actionLoadingId === packId;
              return (
                <QuizTopicCard key={packId || `pack-${index}`}>
                  <QuizTopicHeader>
                    <strong>{pack.name || "Pack de trivia"}</strong>
                  </QuizTopicHeader>
                  {pack.category?.name && <CategoryBadge>{pack.category.name}</CategoryBadge>}

                  <PackActionRow>
                    <AddButton
                      type="button"
                      onClick={() => handleOpenAcquireModal(pack)}
                      disabled={isAcquired || Boolean(actionLoadingId)}
                    >
                      {isAcquired ? "Canjeada" : isActionLoading ? "Canjeando..." : `Canjear - ${TRIVIA_COST_POINTS} Puntos`}
                    </AddButton>
                  </PackActionRow>
                </QuizTopicCard>
              );
            })}
          </PacksGrid>
          <MarketplacePagination
            currentPage={packsPage}
            totalItems={filteredPacks.length}
            pageSize={MARKETPLACE_PAGE_SIZE}
            onPageChange={setPacksPage}
          />
        </>
      )}

      <Modal
        isOpen={Boolean(pendingPack)}
        onClose={handleCloseAcquireModal}
        label="Confirmar canje"
        acceptButtonText={actionLoadingId ? "Canjeando..." : `Canjear ${TRIVIA_COST_POINTS} Puntos`}
        cancelButtonText="Cancelar"
        onAccept={handleAcquirePack}
        onCancel={handleCloseAcquireModal}
        acceptButtonDisabled={Boolean(actionLoadingId)}
        cancelButtonDisabled={Boolean(actionLoadingId)}
        width="500px"
      >
        <ModalText>
          ¿Deseas canjear el pack <strong>{pendingPack?.name || "este pack"}</strong> por{" "}
          <strong>{TRIVIA_COST_POINTS} Puntos</strong>?
          <br />
          <small>Se descontarán de tu saldo disponible.</small>
        </ModalText>
      </Modal>

      <Modal
        isOpen={showNoBalanceModal}
        onClose={() => setShowNoBalanceModal(false)}
        label="Saldo insuficiente"
        acceptButtonText="Entendido"
        onAccept={() => setShowNoBalanceModal(false)}
        width="420px"
      >
        <ModalText>
          No tienes suficientes <strong>Puntos</strong> para comprar este pack.
          <br />
          <small>Cada pack cuesta <strong>{TRIVIA_COST_POINTS} Puntos</strong>.</small>
        </ModalText>
      </Modal>
    </QuizProfileWrapper>
  );
};

export default TriviaMarketplace;

const MarketplaceHeader = styled.header`
  display: grid;
  gap: 6px;
  margin-bottom: 18px;

  h2 {
    margin: 0;
    color: #111827;
    font-size: 26px;
  }

  p {
    margin: 0;
    color: #6b7280;
    font-size: 16px;
    line-height: 1.5;
  }
`;

const PacksGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const PackActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const theme = getHeaderTheme();

const AddButton = styled.button`
  background: ${theme.primary};
  color: ${theme.text};
  border: none;
  border-radius: 999px;
  padding: 10px 22px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.15s ease, filter 0.2s ease;

  &:hover:not(:disabled) {
    filter: brightness(0.92);
    transform: scale(1.02);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 22px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 0 4px;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #777;
  text-align: center;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = styled.button`
  min-width: 32px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: ${({ disabled, $active }) => ($active ? "#111" : disabled ? "#f3f3f3" : "#fff")};
  color: ${({ disabled, $active }) => ($active ? "#fff" : disabled ? "#aaa" : "#333")};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? "700" : "500")};
`;

const ModalText = styled.p`
  margin: 0 0 18px;
  color: #334155;
  font-size: 16px;
  line-height: 1.5;
  text-align: center;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  margin-top: 8px;
  margin-bottom: 0;
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 1.005rem;
  font-weight: 600;
`;
